import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { ContractorEntity, Address } from "@/types/contractor-entity";

// Backend response interface (snake_case)
interface ContractorEntityBackend {
  id: number;
  status: string;
  entity_type: string;
  business_name: string;
  registration_number: string | null;
  trading_name: string | null;
  vat_registered: boolean;
  vat_number: string | null;
  utr_number: string | null;
  registered_address: string;
  trading_address: string | null;
  created_at: string;
  updated_at: string;
}

interface ContractorBackend {
  id: number;
  email: string;
  status: string;
  last_active: string;
  entities: ContractorEntityBackend[];
}

// Transform backend entity to frontend format
const transformEntity = (
  backendEntity: ContractorEntityBackend,
): ContractorEntity => {
  // Parse address string to Address object (simplified - you may need more robust parsing)
  const parseAddress = (addressString: string): Address => {
    // This is a simplified parser - you might need more sophisticated logic
    // depending on how the backend stores the address string
    return {
      route: addressString, // For now, just store the full address as route
      country: "United Kingdom",
      country_code: "GB",
    };
  };

  return {
    id: backendEntity.id,
    status: backendEntity.status,
    businessName: backendEntity.business_name,
    registrationNumber: backendEntity.registration_number || undefined,
    vatRegistered: backendEntity.vat_registered,
    vatNumber: backendEntity.vat_number || undefined,
    yearlyTurnover: undefined, // Not available in backend response
    registeredAddress: parseAddress(backendEntity.registered_address),
    tradingAddress: backendEntity.trading_address
      ? parseAddress(backendEntity.trading_address)
      : undefined,
    createdAt: backendEntity.created_at,
    updatedAt: backendEntity.updated_at,
  };
};

interface Contractor {
  id: number;
  email: string;
  status: string;
  lastActive: string;
  entities: ContractorEntity[];
}

const ContractorEntitiesList: React.FC = () => {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchContractors = async () => {
    try {
      const data = await ApiService.request<{
        contractors: ContractorBackend[];
      }>(EndpointKeys.GET_CONTRACTOR_ENTITIES);

      // Transform backend data to frontend format
      const transformedContractors = data.contractors.map((contractor) => ({
        id: contractor.id,
        email: contractor.email,
        status: contractor.status,
        lastActive: contractor.last_active,
        entities: contractor.entities.map(transformEntity),
      }));

      // Filter out demo contractors and entities created before May 20th, 2025
      const cutoffDate = new Date('2025-05-20');
      const filteredContractors = transformedContractors
        .filter(contractor => 
          // Filter out demo contractors (emails containing "demo-contractor")
          !contractor.email.includes("demo-contractor")
        )
        .map(contractor => ({
          ...contractor,
          entities: contractor.entities.filter(entity => {
            // Filter out entities created before May 20th, 2025
            const createdDate = new Date(entity.createdAt);
            return createdDate >= cutoffDate;
          })
        }))
        .filter(contractor => contractor.entities.length > 0); // Remove contractors with no entities after filtering

      setContractors(filteredContractors);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch contractor entities",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateEntityStatus = async (entityId: number, newStatus: string) => {
    try {
      await ApiService.request(EndpointKeys.UPDATE_CONTRACTOR_ENTITY_STATUS, {
        method: "POST",
        body: { status: newStatus },
        urlParams: { entityId },
      });
      toast({
        title: "Success",
        description: "Entity status updated successfully",
      });
      fetchContractors();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update entity status",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchContractors();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-semibold">Contractor Entities</h2>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contractor</TableHead>
              <TableHead>Business Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Registration</TableHead>
              <TableHead>VAT</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contractors.map((contractor) =>
              contractor.entities.map((entity) => (
                <TableRow key={entity.id}>
                  <TableCell>{contractor.email}</TableCell>
                  <TableCell>{entity.businessName}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        entity.status === "active"
                          ? "default"
                          : entity.status === "pending"
                            ? "secondary"
                            : "destructive"
                      }
                    >
                      {entity.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{entity.registrationNumber || "N/A"}</TableCell>
                  <TableCell>
                    {entity.vatRegistered ? entity.vatNumber : "Not registered"}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      {entity.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() =>
                              updateEntityStatus(entity.id, "active")
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              updateEntityStatus(entity.id, "rejected")
                            }
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {entity.status === "active" && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() =>
                            updateEntityStatus(entity.id, "inactive")
                          }
                        >
                          Deactivate
                        </Button>
                      )}
                      {entity.status === "inactive" && (
                        <Button
                          size="sm"
                          onClick={() =>
                            updateEntityStatus(entity.id, "active")
                          }
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )),
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ContractorEntitiesList;
